import Vue from 'vue';
import Vuex from 'vuex';
import modules from './modules';
import { widgetViews, unparam, sleep, removeCampaignFromUrl, setCampaignInUrl } from '@/utils';
import { getSentryScope } from '@/services/sentry';

Vue.use(Vuex);

const store = new Vuex.Store({
  modules,
  state: {
    orgId: null,
    isWidgetOpen: false,
    widgetView: widgetViews.DONATION_FORM,
    slideDirection: '', // Don't transition on first load
    dtdInfo: {},
    successfullyDonated: false,
    datePickerLibraryLoaded: false,
    showPaymentButtons: false,
    showLocalizationModal: false,
    isPages: false,
    isHostedSurface: false,
    isCustomAmountOpen: false
  },
  mutations: {
    SET_ORG_ID(state, value) {
      state.orgId = value;
    },
    SET_WIDGET_VIEW(state, view) {
      if (!Object.values(widgetViews).includes(view)) {
        throw new Error(`Unknown widget view: ${view}`);
      }
      const slideDirection = view.level > state.widgetView.level ? 'slide-right' : 'slide-left';
      state.slideDirection = slideDirection;
      state.widgetView = view;
    },

    SET_WIDGET_OPEN(state, value) {
      state.isWidgetOpen = value;
    },

    SET_SUCCESSFULLY_DONATED(state, value) {
      state.successfullyDonated = value;
    },
    SET_DTD_INFO(state, newValue) {
      state.dtdInfo = newValue;
    },
    SET_DATE_PICKER_LIBRARY_LOADED(state, newValue) {
      state.datePickerLibraryLoaded = newValue;
    },
    SET_SHOW_PAYMENT_BUTTONS(state, newValue) {
      if (newValue && !state.donation.amount) {
        throw new Error('Amount must be set before showing payment buttons');
      }
      state.showPaymentButtons = newValue;
    },
    SET_SHOW_LOCALIZATION_MODAL(state, newValue) {
      state.showLocalizationModal = newValue;
    },
    SET_IS_PAGES(state, newValue) {
      state.isPages = newValue;
    },
    SET_IS_HOSTED_SURFACE(state, newValue) {
      state.isHostedSurface = newValue;
    },
    SET_CUSTOM_AMOUNT_OPEN(state, newValue) {
      state.isCustomAmountOpen = newValue;
    }
  },
  actions: {
    async setWidgetOpen({ commit, dispatch, state, rootState }, value) {
      commit('SET_WIDGET_OPEN', value);
      const defaultSlug = rootState.publicOrgSettings.settings.defaultSlug;
      const { campaign, fundraiser, solicitor } = unparam(window.location.search);
      if (!campaign) {
        setCampaignInUrl(defaultSlug, value);
      }

      const campaignSlug = campaign || defaultSlug;

      if (value) {
        commit('campaign/RESET_CAMPAIGN'); // Important, so we don't get a flicker
        const campaign = await dispatch('campaign/fetchCampaign', { campaignSlug, withGatewayIntegrations: true });
        commit('donation/SET_CAMPAIGN_ID', campaign.id);

        // Check for fundraiser parameter when widget opens
        const fundraiserSlug = fundraiser || sessionStorage.getItem('Double.fundraiserSlug');
        if (fundraiserSlug) {
          await dispatch('fundraiser/fetchFundraiser', { fundraiserSlug });
          sessionStorage.setItem('Double.fundraiserSlug', fundraiserSlug);
        }

        const solicitorSlug = solicitor || sessionStorage.getItem('Double.solicitorSlug');
        if (solicitorSlug) {
          commit('donation/SET_SOLICITOR_SLUG', solicitorSlug);
          sessionStorage.setItem('Double.solicitorSlug', solicitorSlug);
        }
      } else {
        await sleep(500);
        commit('SET_WIDGET_VIEW', widgetViews.DONATION_FORM);
        commit('campaign/RESET_CAMPAIGN');
        commit('SET_CUSTOM_AMOUNT_OPEN', false);
        removeCampaignFromUrl();

        if (state.successfullyDonated || state.donation.zelleInstructions?.donorReportedSentAt) {
          commit('donation/RESET_STATE');
          commit('SET_SHOW_PAYMENT_BUTTONS', false);
        }
      }
    }
  },
  plugins: [
    (store) => {
      const sentryScope = getSentryScope();
      store.subscribe((mutation) => {
        sentryScope.addBreadcrumb({
          category: 'mutation',
          message: mutation.type,
          data: {
            payload: mutation.payload
          }
        });
      });
      store.subscribeAction((action) => {
        sentryScope.addBreadcrumb({
          category: 'action',
          message: action.type,
          data: {
            payload: action.payload
          }
        });
      });
    }
  ]
});

export default store;
